import { Box } from "@mui/material";
import { type Ref, useId } from "react";
import { useTranslation } from "react-i18next";
import { SITE_TYPE } from "src/domains/shared/constants/styles/site";
import { siteFileUrl } from "../siteKit/media/siteMedia";
import { replayOnEnterRef } from "../siteKit/motion/reveal";
import { chapterNumber, type ManualChapterId } from "./manualChapters";
import { jumpToManualSection } from "./manualJump";
import type { ManualSearchEntry } from "./manualSearchIndex";
import { ManualSearch } from "./ManualSearch";
import { MANUAL_STYLES } from "./manualStyles";
import { MANUAL_CHAPTERS } from "./manualTopics";

const ARROW_PATH = "M6 3l5 5-5 5";

/**
 * 매뉴얼 첫 화면: 제목·검색 + 기운 앱 타일, 아래 여덟 장 두 열 줄("목차"라고 적지 않고 번호·이름·장 제목만, 줄 전체가 눌린다).
 * 손님(`interactive` 아님)에게는 검색 칸이 없고 장 줄은 글자만이라 누르거나 초점을 받지 않는다.
 */
export function ManualHero({
  searchEntries,
  interactive = true,
  chapterLinesRef,
}: {
  searchEntries: readonly ManualSearchEntry[];
  interactive?: boolean;
  /** 여덟 장 줄 묶음 — 손님 안내 띠가 나타날 때를 재는 기준 */
  chapterLinesRef?: Ref<HTMLDivElement>;
}) {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const chapterRow = (id: ManualChapterId, index: number) => (
    <>
      <span data-n="">{chapterNumber(index)}</span>
      <b>{t(`site.manual.chapters.${id}.name`)}</b>
      <span data-sub="">{t(`site.manual.chapters.${id}.subtitle`)}</span>
    </>
  );
  return (
    <Box component="section" aria-labelledby={titleId} sx={MANUAL_STYLES.hero}>
      <Box ref={replayOnEnterRef} sx={MANUAL_STYLES.heroGrid}>
        <Box sx={MANUAL_STYLES.heroText}>
          <Box component="p" sx={SITE_TYPE.eyebrow}>
            {t("site.manual.eyebrow")}
          </Box>
          <Box component="h1" id={titleId} sx={SITE_TYPE.h1}>
            {t("site.manual.title")}
          </Box>
          {interactive ? <ManualSearch entries={searchEntries} /> : null}
        </Box>
        {/* 업데이트 소식과 같은 자리·크기의 둥근 앱 타일 안에 안내 영상 플레이어(빈 화면 가운데 재생 단추와 그것을 누르는 커서·단계 막대) — 바닥 그림자는 움직이지 않는 자리(mark)에, 타일은 그 안에서 떠 있다 */}
        <Box aria-hidden="true" sx={MANUAL_STYLES.mark}>
          <Box sx={MANUAL_STYLES.markTile}>
            {/* eslint-disable-next-line @next/next/no-img-element -- 사이트 미디어는 파이프라인이 만든 해시 파일을 그대로 쓴다(next/image 최적화 대상 아님) */}
            <img
              src={siteFileUrl("art/guide_player.svg")}
              alt=""
              width={360}
              height={250}
            />
          </Box>
        </Box>
      </Box>
      <Box ref={chapterLinesRef} sx={MANUAL_STYLES.chapterLinesWrap}>
        {interactive ? (
          <Box
            component="nav"
            aria-label={t("site.manual.chaptersLabel")}
            sx={MANUAL_STYLES.chapterLines}
          >
            {MANUAL_CHAPTERS.map(({ id }, index) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(event) => {
                  event.preventDefault();
                  jumpToManualSection(id);
                }}
              >
                {chapterRow(id, index)}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  <path
                    d={ARROW_PATH}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            ))}
          </Box>
        ) : (
          <Box component="ul" sx={MANUAL_STYLES.chapterLines}>
            {MANUAL_CHAPTERS.map(({ id }, index) => (
              <li key={id} data-row="">
                {chapterRow(id, index)}
              </li>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
